/**
 * tenant-time — single source of truth for tenant-timezone date/time
 * serialization in the booking flow.
 *
 * WHY THIS EXISTS
 * ───────────────
 * The JS `Date` object is timezone-less: it's a UTC instant under the
 * hood, but every string conversion picks an implicit zone. Naked
 * `format(date, 'yyyy-MM-dd')` from `date-fns` uses the **browser**
 * timezone — which means a customer in Addis Ababa (UTC+3) and a tenant
 * in Berlin (UTC+1/+2) disagree about which calendar day a given
 * instant belongs to, and slots come back for the wrong day.
 *
 * Every conversion of `Date → 'yyyy-MM-dd'` or `Date + 'HH:MM' → UTC`
 * in the booking flow MUST go through this module. The branded
 * `TenantDateString` makes naked strings type-incompatible with our
 * wire/API contracts, so the bug-class is structurally prevented.
 *
 * USAGE
 * ─────
 *   const dateStr = toTenantDateString(selectedDate, tenant.timezone);
 *   // dateStr is `TenantDateString` — accepted by APIs
 *
 *   const utc = combineTenantDateTime(selectedDate, '14:30', tenant.timezone);
 *   // utc is a Date in UTC, safe to send over the wire
 *
 * DO NOT import { format } from 'date-fns' for date serialization in
 * storefront/booking code — ESLint blocks it.
 */
import { format as formatDateFns } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { de, enUS, fr, es, arSA, tr, ru, type Locale } from 'date-fns/locale';

declare const tenantDateBrand: unique symbol;

/**
 * A 'yyyy-MM-dd' string that has been formatted in the tenant's
 * timezone. Branded so naked `string` (e.g. from `format()` or
 * `toISOString().slice(0,10)`) cannot be assigned where this type is
 * required.
 */
export type TenantDateString = string & { readonly [tenantDateBrand]: true };

/** IANA timezone identifier, e.g. `'Europe/Berlin'`. */
export type TenantTimezone = string;

/**
 * Convert a `Date` (or any timestamp accepted by `date-fns-tz`) into a
 * `'yyyy-MM-dd'` string **in the tenant's calendar**.
 *
 * Example: a Date representing 2026-05-26T22:00:00Z (= 27 May 00:00
 * Berlin) returns `'2026-05-27'` for `tz='Europe/Berlin'`, regardless
 * of where the browser thinks it is.
 */
export function toTenantDateString(
  date: Date | number,
  tz: TenantTimezone
): TenantDateString {
  return formatInTimeZone(date, tz, 'yyyy-MM-dd') as TenantDateString;
}

/**
 * Combine a tenant-local date (Date object whose intended day is the
 * one the customer picked in the UI) with an `'HH:MM'` time string and
 * resolve the resulting wall-clock moment to a UTC `Date`.
 *
 * Equivalent to building `${tenantDateStr}T${hhmm}:00` and running
 * `fromZonedTime` — but eliminates the intermediate naked string that
 * is the actual bug surface.
 */
export function combineTenantDateTime(
  date: Date,
  hhmm: string,
  tz: TenantTimezone
): Date {
  const dateStr = toTenantDateString(date, tz);
  return fromZonedTime(`${dateStr}T${hhmm}:00`, tz);
}

/**
 * Build the `'yyyy-MM-ddTHH:MM:00'` local-time string used in some wire
 * payloads (e.g. `anchor.bookingTime` for the Group-Add panel). The
 * server combines this with the tenant timezone server-side.
 *
 * Returned as a plain string (not branded) because it is a composite
 * shape, not a pure date.
 */
export function toTenantLocalDateTimeString(
  date: Date,
  hhmm: string,
  tz: TenantTimezone
): string {
  return `${toTenantDateString(date, tz)}T${hhmm}:00`;
}

/**
 * Best-effort detection of the **browser's** current IANA timezone.
 * Used at signup to pre-fill the tenant's timezone with a sensible
 * default — the new owner can override it later in /admin/settings.
 *
 * Falls back to `'Europe/Berlin'` if the runtime doesn't expose the
 * Intl tzdata (extremely old browsers, restricted execution contexts).
 *
 * IMPORTANT: callable on the client only. Calling this server-side
 * returns the server's process timezone, which is meaningless for the
 * new tenant.
 */
/**
 * Map a Intl-style locale string ('de-DE', 'en', 'fr-FR', …) to a
 * date-fns Locale object. Used by `formatInTimeZone(..., { locale })`
 * to produce localized weekday/month names in emails, ICS payloads,
 * and any other tenant-TZ-aware UI surface.
 *
 * Region-suffix is stripped as a fallback so unknown variants ('de-AT',
 * 'en-AU') map to the closest base language. Defaults to German because
 * 100% of current tenants are DE; if/when that changes, swap the
 * fallback for the actual tenant default.
 */
const DATE_FNS_LOCALE_MAP: Record<string, Locale> = {
  de, 'de-DE': de, 'de-AT': de, 'de-CH': de,
  en: enUS, 'en-US': enUS, 'en-GB': enUS, 'en-AU': enUS,
  fr, 'fr-FR': fr, 'fr-CA': fr,
  es, 'es-ES': es, 'es-MX': es,
  ar: arSA, 'ar-SA': arSA, 'ar-AE': arSA,
  tr, 'tr-TR': tr,
  ru, 'ru-RU': ru,
};

export function dateFnsLocaleFor(intlLocale: string | undefined | null): Locale {
  if (!intlLocale) return de;
  return DATE_FNS_LOCALE_MAP[intlLocale]
    ?? DATE_FNS_LOCALE_MAP[intlLocale.split('-')[0]]
    ?? de;
}

export function detectBrowserTimezone(): TenantTimezone {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && typeof tz === 'string') return tz;
  } catch {
    /* fall through */
  }
  return 'Europe/Berlin';
}

// ─── Tenant-TZ aware calendar grid helpers ──────────────────────────
//
// PROBLEM these solve:
//   date-fns `startOfMonth`/`startOfWeek`/`eachDayOfInterval` work in
//   the **process** timezone (Vercel = UTC). Build a Monday-Sunday
//   grid for a Pacific/Auckland tenant on Vercel, and the first/last
//   week of the grid can shift by one day, because UTC midnight is a
//   different calendar date than tenant-Auckland midnight.
//
// SOLUTION: stay in 'yyyy-MM-dd' string space. Strings are TZ-less;
// they ARE the calendar day. We anchor any Date we need to construct
// at NOON UTC of the date string — noon has a ±12h buffer that
// survives every IANA offset (UTC-12 … UTC+14) and every DST shift
// (≤1h). The day-of-week of '2026-03-01' is Sunday everywhere on
// Earth — no TZ lookup needed.
//
// The grid we hand back is intentionally `TenantDateString[]` so
// downstream rendering doesn't need to convert back into Dates at all
// (the `d` returned is the same string the slot-API expects).

/**
 * Parse a `TenantDateString` into a UTC Date at noon of that day.
 * Used when downstream code requires a `Date` (e.g. React state, the
 * orchestrator's `setSelectedDate(Date|null)` setter). The noon anchor
 * guarantees `formatInTimeZone(result, tz, 'yyyy-MM-dd')` returns the
 * same string for any IANA `tz`.
 */
export function parseDateStrAtNoon(s: TenantDateString | string): Date {
  // Validate the string shape to fail fast on bad input — silently
  // accepting '2026-13-99' would let a corrupt URL break the grid.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    throw new Error(`Invalid TenantDateString: ${s}`);
  }
  return new Date(`${s}T12:00:00Z`);
}

/**
 * Add (or subtract) calendar days to a `TenantDateString`. Pure
 * arithmetic on the noon-anchored Date — DST-safe because noon is far
 * from every wall-clock day boundary.
 */
export function dateStrAddDays(
  s: TenantDateString | string,
  days: number,
): TenantDateString {
  const d = parseDateStrAtNoon(s);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10) as TenantDateString;
}

/**
 * Format a `TenantDateString` with a date-fns pattern + optional
 * locale. Uses the noon-anchored UTC Date, which is safe for any
 * calendar-day-only output (`'d'`, `'PPPP'`, `'d. MMM'`, `'EEEEEE'`).
 *
 * Do NOT use this for any pattern that includes the time-of-day (HH,
 * mm, ss, z, X, …) — the noon anchor is meaningless for those. Use
 * `formatInTimeZone(date, tz, pattern)` from date-fns-tz instead.
 *
 * The wrapper exists so storefront/booking code never has to import
 * `format` from `date-fns` directly (banned by ESLint to prevent
 * accidental browser-TZ regressions).
 */
export function formatTenantDateString(
  s: TenantDateString | string,
  pattern: string,
  locale?: Locale,
): string {
  return formatDateFns(parseDateStrAtNoon(s), pattern, locale ? { locale } : undefined);
}

/**
 * ISO day-of-week of a date string: 1=Mon, 7=Sun. The calendar day's
 * weekday is invariant across timezones — `2026-03-01` is a Sunday
 * everywhere on Earth — so no `tz` parameter needed.
 */
export function dateStrDayOfWeekIso(s: TenantDateString | string): number {
  const sunday0 = parseDateStrAtNoon(s).getUTCDay(); // 0=Sun..6=Sat
  return sunday0 === 0 ? 7 : sunday0;
}

/**
 * The current calendar day in the tenant's timezone, as a `TenantDateString`.
 */
export function tenantToday(tz: TenantTimezone, now: Date = new Date()): TenantDateString {
  return toTenantDateString(now, tz);
}

/**
 * Build the Monday-through-Sunday calendar grid for the month
 * containing `monthAnchor`, anchored in the tenant's timezone.
 *
 * Returns the full grid: first day of the first calendar row (the
 * Monday on/before the 1st of the month) through the last day of the
 * last calendar row (the Sunday on/after the last of the month).
 * Always a multiple of 7 (5 or 6 weeks depending on month layout).
 *
 * @param monthAnchor any `Date` inside the target month, interpreted
 *                    in `tz`. Use `parseDateStrAtNoon('YYYY-MM-01')`
 *                    or just the React `currentMonth` state.
 * @param weekStartsOn `1` = Monday (default), `0` = Sunday.
 */
export function buildTenantCalendarGrid(
  monthAnchor: Date,
  tz: TenantTimezone,
  options: { weekStartsOn?: 0 | 1 } = {},
): TenantDateString[] {
  const weekStartsOn = options.weekStartsOn ?? 1;
  const ym = formatInTimeZone(monthAnchor, tz, 'yyyy-MM');
  const [year, month] = ym.split('-').map(Number);
  const firstOfMonth = `${ym}-01` as TenantDateString;
  // Last day = first of next month minus 1
  const nextFirst =
    month === 12
      ? (`${year + 1}-01-01` as TenantDateString)
      : (`${year}-${String(month + 1).padStart(2, '0')}-01` as TenantDateString);
  const lastOfMonth = dateStrAddDays(nextFirst, -1);

  // Pad start back to weekStartsOn.
  const firstDow = dateStrDayOfWeekIso(firstOfMonth); // 1..7 Mon..Sun
  // Distance from firstDow back to weekStartsOn (1=Mon=>0, 0=Sun=>convert).
  // For Mon-start (weekStartsOn=1): Mon=0, Tue=1, ..., Sun=6.
  // For Sun-start (weekStartsOn=0): Sun=0, Mon=1, ..., Sat=6.
  const back = weekStartsOn === 1 ? firstDow - 1 : firstDow % 7;
  const gridStart = dateStrAddDays(firstOfMonth, -back);

  // Pad end forward to complete the last week.
  const lastDow = dateStrDayOfWeekIso(lastOfMonth);
  // For Mon-start: Sun=7=>0 forward, Sat=6=>1, Fri=5=>2, ..., Mon=1=>6.
  // For Sun-start: Sat=6=>0 forward, Fri=5=>1, ..., Sun=7=>6.
  const fwd = weekStartsOn === 1 ? 7 - lastDow : (6 - lastDow + 7) % 7;
  const gridEnd = dateStrAddDays(lastOfMonth, fwd);

  const grid: TenantDateString[] = [];
  let cur = gridStart;
  // Iterate inclusive of gridEnd. String comparison works because
  // 'yyyy-MM-dd' is lexicographically sortable.
  while (cur <= gridEnd) {
    grid.push(cur);
    cur = dateStrAddDays(cur, 1);
    // Safety net — should never fire (max grid size is 6×7=42).
    if (grid.length > 49) break;
  }
  return grid;
}
